import { useLocalStorage } from "@web-monorepo/safe-browser-storage";
import { useMemo } from "react";

interface FeatureSwitchConfigShape<T extends string> {
  name: T;
  value: string;
  predefinedValues: string[];
  isOverridden: boolean;
}

type HoldoutInfo =
  | { status: "shipped"; holdoutName: string; shippedVariant: string }
  | { status: "active"; holdoutName: string };
export type HoldoutData = Record<string, HoldoutInfo>;
interface FeatureSwitchesSectionVMProps {
  featureSwitches: FeatureSwitchConfigShape<string>[];
  holdoutData: HoldoutData | undefined;
}

function normalizeSearch(search: string) {
  return search.trim().toLowerCase();
}

function doesFeatureSwitchMatchSearch(featureSwitch: FeatureSwitchConfigShape<string>, search: string) {
  const normalizedSearch = normalizeSearch(search);
  return normalizedSearch.length === 0 || featureSwitch.name.toLowerCase().includes(normalizedSearch);
}

// optimization for shifting holdout <-> experiment relation with the minimal amount of loops
export function getHoldoutSwitches(
  featureSwitches: FeatureSwitchConfigShape<string>[],
  holdoutData: HoldoutData | undefined,
) {
  if (!holdoutData) return undefined;
  const switchesByExperimentName = featureSwitches.reduce<Record<string, FeatureSwitchConfigShape<string>>>(
    (acc, fs) => {
      acc[fs.name] = fs;
      return acc;
    },
    {},
  );

  const holdoutSwitches = Object.entries(holdoutData).reduce<
    Record<string, { value: FeatureSwitchConfigShape<string> | undefined; fs: FeatureSwitchConfigShape<string>[] }>
  >((acc, [experimentInHoldoutName, holdoutInfo]) => {
    const holdoutName = holdoutInfo.holdoutName;
    const experimentSwitch = switchesByExperimentName[experimentInHoldoutName];
    if (!experimentSwitch) {
      console.warn(
        `[DevTools] Holdout "${holdoutName}" references experiment "${experimentInHoldoutName}" which has no matching feature switch`,
      );
    }
    if (!acc[holdoutName]) {
      acc[holdoutName] = {
        value: switchesByExperimentName[holdoutName],
        fs: experimentSwitch ? [experimentSwitch] : [],
      };
    } else if (experimentSwitch) {
      acc[holdoutInfo.holdoutName].fs.push(experimentSwitch);
    }
    return acc;
  }, {});
  return holdoutSwitches;
}

export function getFilteredOverrideSwitches(featureSwitches: FeatureSwitchConfigShape<string>[], search: string) {
  return featureSwitches.filter((fs) => fs.isOverridden && doesFeatureSwitchMatchSearch(fs, search));
}

export function getFilteredFeatureSwitches(
  featureSwitches: FeatureSwitchConfigShape<string>[],
  holdoutData: HoldoutData | undefined,
  search: string,
) {
  const holdoutKeys = holdoutData ? Object.keys(holdoutData) : [];

  return featureSwitches
    .filter((fs) => !fs.isOverridden)
    .filter((fs) => !holdoutKeys.includes(fs.name))
    .filter((fs) => doesFeatureSwitchMatchSearch(fs, search));
}

export function getVisibleHoldoutFeatureSwitches(
  featureSwitches: FeatureSwitchConfigShape<string>[],
  holdoutData: HoldoutData | undefined,
  search: string,
) {
  if (!holdoutData) return { holdoutFeatureSwitches: {}, holdoutWarnings: [] as string[] };

  const groupedByHoldout: Record<
    string,
    {
      value: FeatureSwitchConfigShape<string>;
      fs: FeatureSwitchConfigShape<string>[];
    }
  > = {};
  const warnings: string[] = [];

  const holdoutSwitches = getHoldoutSwitches(featureSwitches, holdoutData);

  for (const [, holdoutInfo] of Object.entries(holdoutData)) {
    const holdoutName = holdoutInfo.holdoutName;
    if (groupedByHoldout[holdoutName] || !holdoutSwitches?.[holdoutName]) {
      continue;
    }

    const holdoutSwitch = holdoutSwitches[holdoutName].value;
    if (!holdoutSwitch) {
      warnings.push(`Holdout switch not found: ${holdoutName}`);
      continue;
    }

    const shouldIncludeGroup =
      doesFeatureSwitchMatchSearch(holdoutSwitch, search) ||
      holdoutSwitches[holdoutName].fs.some((fs) => doesFeatureSwitchMatchSearch(fs, search));
    if (!shouldIncludeGroup) {
      continue;
    }

    groupedByHoldout[holdoutName] = {
      value: holdoutSwitch,
      fs: holdoutSwitches[holdoutName].fs,
    };
  }

  return { holdoutFeatureSwitches: groupedByHoldout, holdoutWarnings: warnings };
}

export function useFeatureSwitchesSectionVM(props: FeatureSwitchesSectionVMProps) {
  const [search, setSearch] = useLocalStorage("DevToolsFeatureSwitchSearch", "");

  const overriddenFeatureSwitches = useMemo(
    () => getFilteredOverrideSwitches(props.featureSwitches, search),
    [props.featureSwitches, search],
  );

  const filteredFeatureSwitches = useMemo(
    () => getFilteredFeatureSwitches(props.featureSwitches, props.holdoutData, search),
    [props.featureSwitches, props.holdoutData, search],
  );

  const { holdoutFeatureSwitches, holdoutWarnings } = useMemo(() => {
    return getVisibleHoldoutFeatureSwitches(props.featureSwitches, props.holdoutData, search);
  }, [props.featureSwitches, props.holdoutData, search]);

  return {
    search,
    setSearch,
    overriddenFeatureSwitches,
    filteredFeatureSwitches,
    holdoutFeatureSwitches,
    holdoutWarnings,
  };
}
